import React from 'react';
import type { ReactNode } from 'react';
import { EdgeLabelsContext } from './EdgeLabelsContext';

export interface CanvasLegendEntry {
  /** Display label for the entry (e.g. "Input Data", "Draft"). */
  label: string;
  /** CSS color string for the dot fill, including `var(--token)` refs. */
  color: string;
  /** Optional 1px ring/border color around the dot. */
  ringColor?: string;
  /** Optional CSS class for the label text (e.g. for `capitalize`). */
  labelClassName?: string;
  /** Optional title attribute for hover tooltip. */
  title?: string;
}

interface GraphCanvasProps {
  /** Title shown at the left of the header strip. Optional — when omitted
   *  the title block is not rendered (e.g. the build DAG, whose name lives
   *  in the workspace brief above). */
  title?: string;
  subtitle?: string;
  /**
   * Legend entries shown in the top strip. Pass an empty array (or
   * omit) to hide the legend entirely.
   */
  legend?: CanvasLegendEntry[];
  /** Tailwind classes for the title. Defaults to the research-view size. */
  titleClassName?: string;
  /** Tailwind classes for the subtitle. */
  subtitleClassName?: string;
  /** Controls rendered at the far right of the header strip, alongside the
   *  edge-labels toggle (e.g. the build run/history/done controls). */
  headerActions?: ReactNode;
  /** Optional full-width band rendered BETWEEN the header strip and the canvas
   *  (e.g. the live run-progress bar). Sits above the canvas — never overlaps
   *  the frozen column header inside it. Render nothing when not running. */
  progressBar?: ReactNode;
  showEdgeLabels: boolean;
  onToggleEdgeLabels: (next: boolean) => void;
  /** Hide the global "Edge labels" checkbox. The production build graph sets
   *  this — it surfaces labels on hover/select via an overlay (owner #3), so a
   *  global toggle is redundant. Other graphs (evidence board) keep the toggle. */
  hideEdgeLabelToggle?: boolean;
  children: ReactNode;
}

const DEFAULT_TITLE_CLASS = 'truncate text-[15px] leading-5 font-semibold text-fg';
const DEFAULT_SUBTITLE_CLASS = 'mt-0.5 truncate text-[11px] leading-4 text-fg-secondary';

export default function GraphCanvas({
  title,
  subtitle,
  legend,
  titleClassName,
  subtitleClassName,
  headerActions,
  progressBar,
  showEdgeLabels,
  onToggleEdgeLabels,
  hideEdgeLabelToggle,
  children,
}: GraphCanvasProps) {
  const legendEntries = legend ?? [];

  return (
    <section
      className="h-full min-h-0 w-full flex flex-col rounded-lg border p-3 bg-surface"
      style={{
        borderColor: 'var(--border)',
        background: 'var(--surface)',
        boxShadow: 'var(--shadow-xs)',
      }}
      aria-label={title || 'Graph canvas'}
    >
      <div className="mb-3 flex flex-shrink-0 flex-wrap items-center gap-x-4 gap-y-2">
        {(title || subtitle) && (
          <div className="min-w-0 flex-1">
            {title && (
              <h2 className={titleClassName ?? DEFAULT_TITLE_CLASS}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={subtitleClassName ?? DEFAULT_SUBTITLE_CLASS}>
                {subtitle}
              </p>
            )}
          </div>
        )}

        {legendEntries.length > 0 && (
          <div className="flex min-h-[24px] flex-wrap items-center gap-x-3 gap-y-1.5">
            {legendEntries.map((entry) => (
              <span
                key={entry.label}
                className="inline-flex items-center gap-1.5 text-[11px] leading-none text-fg-secondary"
                title={entry.title}
              >
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{
                    background: entry.color,
                    border: entry.ringColor ? `1px solid ${entry.ringColor}` : undefined,
                  }}
                />
                <span className={entry.labelClassName}>{entry.label}</span>
              </span>
            ))}
          </div>
        )}

        {/* Right cluster — edge-labels toggle + optional header actions
            (the build run controls). ml-auto right-aligns it whether or not
            a title block is present. */}
        <div className="ml-auto flex flex-shrink-0 items-center gap-3">
          {!hideEdgeLabelToggle && (
            <label className="inline-flex min-h-[24px] items-center gap-2 text-[11px] leading-none text-fg-secondary">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-border-default accent-[var(--secondary)]"
                checked={showEdgeLabels}
                onChange={(event) => onToggleEdgeLabels(event.target.checked)}
              />
              <span>Edge labels</span>
            </label>
          )}
          {headerActions}
        </div>
      </div>

      {progressBar}

      <EdgeLabelsContext.Provider value={{ visible: showEdgeLabels }}>
        <div className="min-h-0 flex-1 overflow-hidden rounded-md">
          {children}
        </div>
      </EdgeLabelsContext.Provider>
    </section>
  );
}
